import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";
import { useCallback, useRef, useState, type ReactNode } from "react";

interface ConfirmOptions {
  title: string;
  content: string;
  confirmLabel: string;
  cancelLabel: string;
  intent?: "default" | "destructive";
}

type PendingConfirm = ConfirmOptions | null;

export interface ConfirmDialogController {
  confirm: (options: ConfirmOptions) => Promise<boolean>;
  confirmDialog: ReactNode;
}

export const useConfirmDialog = (): ConfirmDialogController => {
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const resolve = useCallback((value: boolean) => {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setPendingConfirm(null);
  }, []);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    setPendingConfirm(options);
    return new Promise<boolean>((resolvePromise) => {
      resolverRef.current = resolvePromise;
    });
  }, []);

  const confirmDialog = (
    <Dialog
      open={pendingConfirm !== null}
      onOpenChange={(_, data) => {
        if (!data.open) {
          resolve(false);
        }
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{pendingConfirm?.title || ""}</DialogTitle>
          <DialogContent>{pendingConfirm?.content || ""}</DialogContent>
          {/* fluid: without it the actions row only gets half the dialog's
              width, and a long confirm label wraps onto two lines. */}
          <DialogActions fluid>
            <Button appearance="secondary" onClick={() => resolve(false)}>
              {pendingConfirm?.cancelLabel || "Cancel"}
            </Button>
            <Button
              appearance={
                pendingConfirm?.intent === "destructive" ? "primary" : "primary"
              }
              className={
                pendingConfirm?.intent === "destructive"
                  ? "confirm-dialog__danger"
                  : undefined
              }
              onClick={() => resolve(true)}
            >
              {pendingConfirm?.confirmLabel || "Confirm"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );

  return {
    confirm,
    confirmDialog,
  };
};
